/**
 * ONE FEATURE'S PAGE, under its head: how common it is, who ships it and in
 * which plan, who of the same kind does not (and whether that is known or
 * just unread), the pains it answers, the room's ideas that need it, and the
 * features it may be a copy of. Built by `reports._feature_profile`.
 */
import { useState, type ReactNode } from 'react';
import type { BoardroomFeaturePeer, BoardroomFeatureProfile, BoardroomFeatureShipper } from '../../../types';
import { KIND_LABELS } from '../kinds';
import { VerdictBadge } from '../ItemCard';
import { useFreshClass } from './freshness';

/** How many of a peer group are shown before "show all". */
const PEERS_SHOWN = 24;

const PEER_GROUPS: { state: BoardroomFeaturePeer['state']; heading: string; note: string }[] = [
  { state: 'lists_others', heading: 'List other features, not this one', note: 'their features are recorded, and this is not among them' },
  { state: 'nothing_recorded', heading: 'Read, but no features recorded', note: 'we do not know yet' },
  { state: 'not_read', heading: 'Not read yet', note: 'we do not know yet' },
];

/** How common a feature is, in words, by the share of products that ship it
 *  among those whose features are known. Pure. */
export function howCommon(share: number): string {
  if (share >= 0.5) return 'Most products ship it: table stakes';
  if (share >= 0.2) return 'Common';
  if (share >= 0.05) return 'Some products ship it';
  return 'Rare: few products ship it';
}

/** Which plan holds the feature, in words. Pure. */
export function planWords(s: BoardroomFeatureShipper): string {
  if (s.plan) {
    if (s.cheapest && s.plan.id === s.cheapest.id) return `in the entry plan (${s.plan.label})`;
    return `from ${s.plan.title} (${s.plan.label})`;
  }
  if (s.plans_list_features) return 'no plan lists it';
  return s.cheapest ? `plans start ${s.cheapest.label}` : 'no pricing recorded';
}

function pct(n: number): string {
  if (n > 0 && n < 0.01) return '<1%';
  return `${Math.round(n * 100)}%`;
}

function Card({ title, children, testId }: { title: ReactNode; children: ReactNode; testId?: string }) {
  return (
    <section className="shrink-0 border border-gray-200 dark:border-gray-700 rounded-xl bg-white dark:bg-gray-800 p-3" data-testid={testId}>
      <h3 className="text-[11px] uppercase tracking-wide text-gray-400 mb-1.5">{title}</h3>
      {children}
    </section>
  );
}

function Link({ id, children, onOpen, className = '' }: { id: number; children: ReactNode; onOpen: (id: number) => void; className?: string }) {
  return (
    <button type="button" onClick={() => onOpen(id)} className={`text-left hover:underline ${className}`}>
      {children}
    </button>
  );
}

function Summary({ data }: { data: BoardroomFeatureProfile }) {
  const { products, shipped_by: shipped } = data.totals;
  const lacking = data.peers.filter(p => p.state === 'lists_others').length;
  const unknown = data.peers.length - lacking;
  // AMONG THOSE WE KNOW ABOUT. A product nobody has read for features is not
  // one that lacks this one, so it is left out of the rate.
  const known = shipped + lacking;
  const share = known ? shipped / known : 0;
  return (
    <section className="shrink-0 rounded-xl border border-indigo-200 bg-indigo-50/60 p-3 dark:border-indigo-800 dark:bg-indigo-900/10" data-testid="feature-summary">
      <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
        <span className="text-lg font-semibold text-indigo-900 dark:text-indigo-100">
          Shipped by {shipped} product{shipped === 1 ? '' : 's'}
        </span>
        <span className="text-xs text-gray-600 dark:text-gray-300">
          of {products} on the board ({pct(data.totals.share)})
        </span>
      </div>
      {known > 0 && data.peers.length > 0 && (
        <>
          <div className="mt-2 h-1.5 w-full max-w-md overflow-hidden rounded-full bg-gray-200 dark:bg-gray-700" aria-hidden>
            <div className="h-full rounded-full bg-indigo-500" style={{ width: `${Math.max(2, share * 100)}%` }} />
          </div>
          <p className="mt-1.5 text-xs text-gray-700 dark:text-gray-200" data-testid="feature-summary-known">
            <span className="font-medium">{howCommon(share)}.</span> Of the {known} products of the same kind whose features are recorded,{' '}
            {shipped} ship{shipped === 1 ? 's' : ''} it ({pct(share)}).
            {unknown > 0 && ` ${unknown} more have not had their features recorded yet.`}
          </p>
        </>
      )}
    </section>
  );
}

function Shippers({ rows, onOpen }: { rows: BoardroomFeatureShipper[]; onOpen: (id: number) => void }) {
  const glow = useFreshClass();
  if (!rows.length) return <span className="text-xs text-gray-400 italic">no product is recorded as shipping it</span>;
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-xs" data-testid="feature-shippers">
        <thead>
          <tr className="text-left text-[11px] uppercase tracking-wide text-gray-400">
            <th className="py-1 pr-3 font-normal">Product</th>
            <th className="py-1 pr-3 font-normal">Made by</th>
            <th className="py-1 pr-3 font-normal">Kind</th>
            <th className="py-1 font-normal">Which plan</th>
          </tr>
        </thead>
        <tbody>
          {rows.map(r => (
            <tr key={r.id} className={`border-t border-gray-100 align-baseline dark:border-gray-700/60 ${glow(r.id)}`}>
              <td className="py-1 pr-3">
                <Link id={r.id} onOpen={onOpen} className="font-medium text-gray-900 dark:text-gray-100">
                  {r.title}
                </Link>
                {r.verdict && <span className="ml-1"><VerdictBadge verdict={r.verdict} /></span>}
              </td>
              <td className="py-1 pr-3 text-gray-600 dark:text-gray-300">
                {r.company ? <Link id={r.company.id} onOpen={onOpen}>{r.company.title}</Link> : <span className="text-gray-400">unknown</span>}
              </td>
              <td className="py-1 pr-3 text-gray-600 dark:text-gray-300">
                {r.type ? <Link id={r.type.id} onOpen={onOpen}>{r.type.title}</Link> : <span className="text-gray-400">not filed</span>}
              </td>
              <td className="py-1 text-gray-600 dark:text-gray-300" data-testid={`feature-plan-${r.id}`}>
                {r.plan ? (
                  <Link id={r.plan.id} onOpen={onOpen} className="text-orange-700 dark:text-orange-300">
                    {planWords(r)}
                  </Link>
                ) : (
                  <span className="text-gray-400">{planWords(r)}</span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Peers({ peers, onOpen }: { peers: BoardroomFeaturePeer[]; onOpen: (id: number) => void }) {
  const [open, setOpen] = useState<Record<string, boolean>>({});
  const glow = useFreshClass();
  return (
    <div className="flex flex-col gap-2">
      {PEER_GROUPS.map(g => {
        const rows = peers.filter(p => p.state === g.state);
        if (!rows.length) return null;
        const all = open[g.state];
        return (
          <div key={g.state} data-testid={`feature-peers-${g.state}`}>
            <h4 className="text-xs text-gray-600 dark:text-gray-300">
              <span className="font-medium">{g.heading}</span> · {rows.length}
              <span className="text-gray-400"> · {g.note}</span>
            </h4>
            <ul className="mt-1 flex flex-wrap gap-1">
              {(all ? rows : rows.slice(0, PEERS_SHOWN)).map(p => (
                <li key={p.id}>
                  <button
                    type="button"
                    onClick={() => onOpen(p.id)}
                    title={p.type.title}
                    className={`rounded-full border px-1.5 py-0.5 text-xs hover:bg-gray-50 dark:hover:bg-gray-700 ${glow(p.id)} ${
                      g.state === 'lists_others'
                        ? 'border-gray-300 text-gray-700 dark:border-gray-600 dark:text-gray-200'
                        : 'border-dashed border-gray-300 text-gray-400 dark:border-gray-600'
                    }`}
                  >
                    {p.title}
                  </button>
                </li>
              ))}
              {rows.length > PEERS_SHOWN && (
                <li>
                  <button
                    type="button"
                    onClick={() => setOpen(o => ({ ...o, [g.state]: !all }))}
                    className="px-1.5 py-0.5 text-xs text-indigo-600 hover:underline dark:text-indigo-300"
                  >
                    {all ? 'show fewer' : `show all ${rows.length}`}
                  </button>
                </li>
              )}
            </ul>
          </div>
        );
      })}
    </div>
  );
}

export default function FeatureProfile({
  data,
  onOpen,
  onMerge,
  onKeepApart,
}: {
  data: BoardroomFeatureProfile;
  onOpen: (id: number) => void;
  /** Fold these features into this one. Absent where the page cannot act. */
  onMerge?: (otherIds: number[]) => Promise<void>;
  /** Say these features are different things from this one. */
  onKeepApart?: (otherIds: number[]) => Promise<void>;
}) {
  const [busy, setBusy] = useState(false);
  // TICK, THEN ONE PRESS. A button per row meant a press and a reload per
  // near-copy, and the list was usually all copies.
  const [picked, setPicked] = useState<ReadonlySet<number>>(new Set());
  const glow = useFreshClass();
  const nearIds = data.near.map(n => n.id);
  const chosen = nearIds.filter(id => picked.has(id));
  const allPicked = nearIds.length > 0 && chosen.length === nearIds.length;
  const toggle = (id: number) =>
    setPicked(p => {
      const next = new Set(p);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  const act = (fn?: (ids: number[]) => Promise<void>) => {
    if (!fn || !chosen.length) return;
    setBusy(true);
    fn(chosen).finally(() => {
      setBusy(false);
      setPicked(new Set());
    });
  };
  const canAct = Boolean(onMerge || onKeepApart);
  const typeNames = Array.from(new Set(data.peers.map(p => p.type.title)));
  return (
    <>
      <Summary data={data} />

      <Card title={`Who ships it · ${data.shippers.length}`} testId="feature-who-ships">
        <Shippers rows={data.shippers} onOpen={onOpen} />
      </Card>

      {data.near.length > 0 && (
        <Card title="Possibly the same feature" testId="feature-near">
          <p className="mb-1.5 text-xs text-gray-500 dark:text-gray-400">
            Close titles that were recorded as separate features. Merging moves their products here.
          </p>
          {canAct && (
            <div className="mb-1.5 flex flex-wrap items-center gap-2 text-xs" data-testid="feature-near-actions">
              <label className="inline-flex items-center gap-1.5 text-gray-600 dark:text-gray-300">
                <input
                  type="checkbox"
                  checked={allPicked}
                  disabled={busy}
                  onChange={() => setPicked(allPicked ? new Set() : new Set(nearIds))}
                  className="h-4 w-4"
                  data-testid="feature-near-all"
                />
                Select all
              </label>
              {onMerge && (
                <button
                  type="button"
                  disabled={busy || !chosen.length}
                  onClick={() => act(onMerge)}
                  className="rounded border border-indigo-300 bg-indigo-50 px-2 py-0.5 text-indigo-800 hover:bg-indigo-100 disabled:opacity-50 dark:border-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-200"
                  data-testid="feature-merge-picked"
                >
                  {busy ? 'Working…' : chosen.length ? `Merge ${chosen.length} into this` : 'Merge into this'}
                </button>
              )}
              {onKeepApart && (
                <button
                  type="button"
                  disabled={busy || !chosen.length}
                  onClick={() => act(onKeepApart)}
                  className="rounded border border-gray-300 px-2 py-0.5 text-gray-700 hover:bg-gray-50 disabled:opacity-50 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700"
                  data-testid="feature-apart-picked"
                >
                  {chosen.length ? `${chosen.length} ${chosen.length === 1 ? 'is' : 'are'} different` : 'Different'}
                </button>
              )}
            </div>
          )}
          <ul className="flex flex-col gap-0.5">
            {data.near.map(n => (
              <li key={n.id} className={`flex flex-wrap items-center gap-2 text-xs rounded ${glow(n.id)}`} data-testid={`feature-near-${n.id}`}>
                {canAct && (
                  <input
                    type="checkbox"
                    checked={picked.has(n.id)}
                    disabled={busy}
                    onChange={() => toggle(n.id)}
                    aria-label={`Pick ${n.title}`}
                    className="h-4 w-4"
                    data-testid={`feature-near-pick-${n.id}`}
                  />
                )}
                <Link id={n.id} onOpen={onOpen} className="text-gray-800 dark:text-gray-200">
                  {n.title}
                </Link>
                <span className="text-[11px] text-gray-400">
                  {n.products} product{n.products === 1 ? '' : 's'}
                </span>
              </li>
            ))}
          </ul>
        </Card>
      )}

      {data.peers.length > 0 && (
        <Card
          title={`Same kind, not shipping it · ${data.peers.length}`}
          testId="feature-peers"
        >
          {typeNames.length > 0 && (
            <p className="mb-1.5 text-xs text-gray-500 dark:text-gray-400">
              Products filed as {typeNames.join(', ')}, the same kind as the ones that ship it.
            </p>
          )}
          <Peers peers={data.peers} onOpen={onOpen} />
        </Card>
      )}

      {data.pains.length > 0 && (
        <Card title={`Pains it answers · ${data.pains.length}`} testId="feature-pains">
          <ul className="flex flex-col gap-1">
            {data.pains.map(p => (
              <li key={p.id} className={`flex items-baseline gap-1.5 text-xs rounded ${glow(p.id)}`}>
                <Link id={p.id} onOpen={onOpen} className="text-gray-800 dark:text-gray-200">
                  {p.title}
                </Link>
                <span className="whitespace-nowrap text-[11px] text-gray-400">
                  {p.evidence} piece{p.evidence === 1 ? '' : 's'} of evidence
                </span>
                {p.verdict && <VerdictBadge verdict={p.verdict} />}
              </li>
            ))}
          </ul>
        </Card>
      )}

      {data.ideas.length > 0 && (
        <Card title="Our ideas that need it" testId="feature-ideas">
          <ul className="flex flex-wrap gap-1">
            {data.ideas.map(i => (
              <li key={i.id} className={`rounded ${glow(i.id)}`}>
                <button
                  type="button"
                  onClick={() => onOpen(i.id)}
                  className="rounded-full border border-indigo-200 px-1.5 py-0.5 text-xs text-indigo-700 hover:bg-indigo-50 dark:border-indigo-800 dark:text-indigo-300 dark:hover:bg-indigo-900/20"
                >
                  {i.title}
                </button>
              </li>
            ))}
          </ul>
        </Card>
      )}

      {data.observations.length > 0 && (
        <Card title="Findings that mention it" testId="feature-observations">
          <ul className="flex flex-col gap-0.5 text-xs">
            {data.observations.map(o => (
              <li key={o.id} className={`rounded ${glow(o.id)}`}>
                <Link id={o.id} onOpen={onOpen} className="text-gray-800 dark:text-gray-200">
                  {o.title}
                </Link>
              </li>
            ))}
          </ul>
        </Card>
      )}

      {data.found_under.length > 0 && (
        <Card title="Found while working on" testId="feature-found-under">
          <p className="mb-1 text-xs text-gray-500 dark:text-gray-400">Where it was noticed. These do not ship it.</p>
          <ul className="flex flex-col gap-0.5 text-xs">
            {data.found_under.map(f => (
              <li key={f.id} className={`rounded ${glow(f.id)}`}>
                <Link id={f.id} onOpen={onOpen} className="text-gray-700 dark:text-gray-300">
                  {KIND_LABELS[f.kind]} · {f.title}
                </Link>
              </li>
            ))}
          </ul>
        </Card>
      )}
    </>
  );
}
